/**
 * Country → money, units, paper.
 *
 * A shop is created with a country once, and that country decides how every
 * figure in the product is written (FR48a, FR48b). Currency is a property of the
 * tenant, never of a job — and an estimate that has been issued keeps the
 * currency it was issued in, so this module never guesses from "now".
 *
 * Pure. No React, no database, no globals.
 */

export interface CountrySpec {
  code: string;
  name: string;
  currencyCode: string;
  currencySymbol: string;
  symbolPosition: 'before' | 'after';
  locale: string;
  taxName: string;
  defaultTaxRate: number;
  distanceUnit: 'km' | 'mi';
  paperSize: 'A4' | 'Letter';
}

/**
 * The launch set. This is a mirror of `platform.countries` — the database is the
 * source of truth at runtime; this exists so the client can format before it has
 * fetched anything, and so the values are testable without a database.
 */
export const COUNTRIES: Record<string, CountrySpec> = {
  US: { code: 'US', name: 'United States', currencyCode: 'USD', currencySymbol: '$',
        symbolPosition: 'before', locale: 'en-US', taxName: 'Sales tax',
        defaultTaxRate: 0, distanceUnit: 'mi', paperSize: 'Letter' },
  CA: { code: 'CA', name: 'Canada', currencyCode: 'CAD', currencySymbol: '$',
        symbolPosition: 'before', locale: 'en-CA', taxName: 'GST',
        defaultTaxRate: 5, distanceUnit: 'km', paperSize: 'Letter' },
  GB: { code: 'GB', name: 'United Kingdom', currencyCode: 'GBP', currencySymbol: '£',
        symbolPosition: 'before', locale: 'en-GB', taxName: 'VAT',
        defaultTaxRate: 20, distanceUnit: 'mi', paperSize: 'A4' },
  DE: { code: 'DE', name: 'Germany', currencyCode: 'EUR', currencySymbol: '€',
        symbolPosition: 'after', locale: 'de-DE', taxName: 'MwSt',
        defaultTaxRate: 19, distanceUnit: 'km', paperSize: 'A4' },
  NO: { code: 'NO', name: 'Norway', currencyCode: 'NOK', currencySymbol: 'kr',
        symbolPosition: 'after', locale: 'nb-NO', taxName: 'MVA',
        defaultTaxRate: 25, distanceUnit: 'km', paperSize: 'A4' },
  AE: { code: 'AE', name: 'United Arab Emirates', currencyCode: 'AED', currencySymbol: 'د.إ',
        symbolPosition: 'after', locale: 'en-AE', taxName: 'VAT',
        defaultTaxRate: 5, distanceUnit: 'km', paperSize: 'A4' },
  SA: { code: 'SA', name: 'Saudi Arabia', currencyCode: 'SAR', currencySymbol: '﷼',
        symbolPosition: 'after', locale: 'en-SA', taxName: 'VAT',
        defaultTaxRate: 15, distanceUnit: 'km', paperSize: 'A4' },
};

export function country(code: string): CountrySpec {
  const c = COUNTRIES[code];
  if (!c) throw new Error(`Unknown country code: ${code}`);
  return c;
}

/** Non-breaking space, so a symbol never wraps away from its number. */
const NBSP = ' ';

/**
 * Format money for a country.
 *
 * Deliberately NOT `Intl.NumberFormat(style:'currency')`: that decides symbol
 * placement and the symbol itself for us, and disagrees with what shops print —
 * it renders CAD as "CA$" and AED as "AED". The shop's own symbol and side are
 * the ones on the invoice, so we take the grouping and decimals from Intl and
 * apply the symbol ourselves.
 *
 * Digits stay Western (`latn`) even for Arabic-locale shops: money on an
 * estimate is read as digits, and Eastern Arabic numerals on a price line is a
 * common and much-disliked bug (FR53).
 */
export function formatMoney(amount: number, spec: CountrySpec): string {
  const n = Number.isFinite(amount) ? amount : 0;
  // The sign leads the whole value. Formatting the signed number and then
  // prepending the symbol yields "$-200.00", which is wrong on a printed
  // estimate — and credits are real here (betterment, a returned part).
  const sign = n < 0 ? '-' : '';
  const body = new Intl.NumberFormat(`${spec.locale}-u-nu-latn`, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
  return spec.symbolPosition === 'before'
    ? `${sign}${spec.currencySymbol}${body}`
    : `${sign}${body}${NBSP}${spec.currencySymbol}`;
}

/** Hours as the trade writes them: one decimal, always shown. */
export function formatHours(hours: number, spec: CountrySpec): string {
  const n = Number.isFinite(hours) ? hours : 0;
  return new Intl.NumberFormat(`${spec.locale}-u-nu-latn`, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n);
}

export function formatDistance(value: number, spec: CountrySpec): string {
  const n = Number.isFinite(value) ? value : 0;
  const body = new Intl.NumberFormat(`${spec.locale}-u-nu-latn`, {
    maximumFractionDigits: 0,
  }).format(n);
  return `${body}${NBSP}${spec.distanceUnit}`;
}
